import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import CardInfo from '../../../../../components/CardInfo/CardInfo'
import InputText from '../../../../../components/InputText/InputText'
import CustomDivider from '../../../../../components/CardInfo/components/CustomDivider'
import DragAndDrop from '../../../../../components/DragAndDrop/DragAndDrop'
import { useFormik } from 'formik'
import {
    initialValues,
    validationSchema,
} from '../../../../../forms/CreatePartsQuoteForm'
import useUploadFile from '../../../../../hooks/useUploadFile'
import useGlobalLoading from '../../../../../hooks/useGlobalLoading'
import { XIcon } from '@heroicons/react/solid'
import CellLocation from '../../../../../components/Table/components/CellLocation'
import DropDown from '../../../../../components/DropDown/DropDown'
import useAuth from '../../../../../hooks/useAuth'
import InputNumber from '../../../../../components/InputNumber/InputNumber'
import InputDate from '../../../../../components/InputDate/InputDate'
import useScreen from '../../../../../hooks/useScreen'
import { useNetworkStatus } from '../../../../../utils/networkStatus'
import { removeStepsAndRequests } from '../../../../../utils/handleOnDone'

const ImageItem = ({ src, onRemove }) => {
    return (
        <div className="lg:w-16 lg:h-16 w-auto h-auto group overflow-visible relative">
            <div
                onClick={onRemove}
                role="button"
                className="w-6 h-6 flex items-center justify-center rounded-full bg-error cursor-pointer  absolute -top-3 -right-3"
            >
                <XIcon className="text-white w-4 h-4" />
            </div>
            <img src={src} className="object-cover w-full h-full rounded-lg" />
        </div>
    )
}

ImageItem.propTypes = {
    src: PropTypes.string,
    onRemove: PropTypes.oneOfType([PropTypes.func]),
}

const RenderCustomCard = ({ title, content }) => {
    return (
        <div className="w-full h-auto flex flex-col items-center justify-start overflow-hidden lg:rounded-3xl lg:border border-grayLines">
            <div className="w-full h-auto bg-pgBackground lg:p-6 p-4 lg:border-0 border-b border-grayLines">
                <p className="text-black70 text-base font-medium">{title}</p>
            </div>
            <div className="w-full h-auto flex flex-col items-center justify-start bg-white lg:p-6 p-4">
                {content}
            </div>
        </div>
    )
}

RenderCustomCard.propTypes = {
    title: PropTypes.string,
    content: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.element,
        PropTypes.any,
    ]),
}

const CreatePartsQuoteForm = ({
    formRef,
    onSubmitForm,
    setError,
    setIsValidForm,
    asset,
    successfulSubmit,
    setSuccessfulSubmit,
    resetTouch,
    setResetTouch,
    images,
    photosIds,
    setImages,
    setPhotosIds,
    trackSubmission,
    error,
}) => {
    const [filesSuccess, setFileSuccess] = useState([])
    const { handleUpdateFile } = useUploadFile()
    const { changeLoading } = useGlobalLoading()
    const { user } = useAuth()
    const { isMobile, isTablet } = useScreen()
    const { isOffline } = useNetworkStatus()

    const selectOptions = user?.locationPreferences || []
    const [storedValues, setStoredValues] = useState(() => {
        const savedState = localStorage.getItem('partsRequestForm')
        return savedState ? JSON.parse(savedState) : initialValues
    })

    // Track form submission to prevent duplicates
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Use a ref to track submission - more reliable than state for rapid events
    const submissionInProgressRef = useRef(false)

    const {
        getFieldProps,
        values,
        errors,
        setFieldValue,
        touched,
        handleSubmit,
        isValid,
        resetForm,
        setTouched,
    } = useFormik({
        enableReinitialize: true,
        initialValues: {
            ...storedValues,
            make: asset
                ? asset.manufacturer !== 'Other'
                    ? `${asset.manufacturer || ''} ${asset.simpleType ||
                        ''}`.trim()
                    : asset.simpleType || ''
                : storedValues.make || '',
            model: asset?.model || storedValues.model || '',
        },
        validationSchema,
        onSubmit: formValues => {
            // Use ref to prevent duplicate submissions (more reliable than state)
            if (submissionInProgressRef.current) {
                console.log('Duplicate submission prevented via ref')
                return
            }

            // Set both ref and state to track submission
            submissionInProgressRef.current = true
            setIsSubmitting(true)

            try {
                // Process the form submission
                onSubmitForm(formValues)
            } catch (error) {
                console.error('Error submitting form:', error)
            } finally {
                // Reset submission state after a delay
                setTimeout(() => {
                    submissionInProgressRef.current = false
                    setIsSubmitting(false)
                }, 2000) // Longer timeout to ensure processing completes
            }
        },
    })

    // Special handler for form submission that uses the ref directly
    const handleFormSubmit = e => {
        // Check both ref and state to prevent duplicate submissions
        if (submissionInProgressRef.current || isSubmitting) {
            e.preventDefault()
            e.stopPropagation()
            return false
        }

        handleSubmit(e)
        return false // Prevent natural form submission
    }

    // Add UI feedback for submission state
    useEffect(() => {
        if (isSubmitting) {
            // Could add UI feedback here, like disabling inputs or showing a spinner
            console.log('Form is currently submitting - UI could reflect this')
        }
    }, [isSubmitting])

    useEffect(() => {
        if (error) {
            submissionInProgressRef.current = false
            setIsSubmitting(false)
        }
    }, [error])

    // Cleanup submission state on unmount
    useEffect(() => {
        return () => {
            // Reset submission tracking when component unmounts
            submissionInProgressRef.current = false
            setIsSubmitting(false)
        }
    }, [])

    useEffect(() => {
        if (successfulSubmit) {
            resetForm({
                values: storedValues,
            })
            setSuccessfulSubmit(false)
            setIsValidForm(false)
            removeStepsAndRequests()
        }
    }, [successfulSubmit])

    useEffect(() => {
        if (Object.keys(values).length > 0) {
            // Ensure make and model are properly formatted before storing
            const valuesToStore = {
                ...values,
                // Ensure make is not "undefined undefined"
                make: values.make?.includes('undefined')
                    ? asset?.manufacturer && asset?.manufacturer !== 'Other'
                        ? `${asset.manufacturer} ${asset.simpleType ||
                            ''}`.trim()
                        : asset?.simpleType || values.make
                    : values.make,
                // Keep model value or use asset model as fallback
                model: values.model || asset?.model || '',
                // Format date for storage
                neededByDate:
                    values.neededByDate instanceof Date
                        ? values.neededByDate.toISOString()
                        : values.neededByDate,
            }

            const lastSavedState = localStorage.getItem('partsRequestForm')
            if (lastSavedState !== JSON.stringify(valuesToStore)) {
                localStorage.setItem(
                    'partsRequestForm',
                    JSON.stringify(valuesToStore),
                )
            }
        }
    }, [values, asset])

    useEffect(() => {
        const savedState = localStorage.getItem('partsRequestForm')
        if (savedState) {
            const parsedValues = JSON.parse(savedState)
            if (parsedValues.neededByDate) {
                parsedValues.neededByDate = new Date(parsedValues.neededByDate)
            }

            // Ensure asset make/model are preserved
            const updatedValues = {
                ...parsedValues,
                // Preserve make field with asset data
                make:
                    asset && asset?.manufacturer
                        ? asset.manufacturer !== 'Other'
                            ? `${asset.manufacturer} ${asset.simpleType ||
                                ''}`.trim()
                            : asset.simpleType || ''
                        : parsedValues.make,
                // Preserve model field with asset data
                model: asset?.model || parsedValues.model,
            }

            setStoredValues(updatedValues)
            resetForm({ values: updatedValues })
        }
    }, [isOffline, asset])

    useEffect(() => {
        if (values.neededByDate && !(values.neededByDate instanceof Date)) {
            setFieldValue('neededByDate', new Date(values.neededByDate))
        }
    }, [values.neededByDate])

    // Update make and model whenever asset changes
    useEffect(() => {
        if (asset && (asset.manufacturer || asset.simpleType || asset.model)) {
            // Only update if the current values are empty or contain "undefined"
            if (!values.make || values.make.includes('undefined')) {
                const newMake =
                    asset.manufacturer !== 'Other'
                        ? `${asset.manufacturer || ''} ${asset.simpleType ||
                            ''}`.trim()
                        : asset.simpleType || ''

                setFieldValue('make', newMake)
            }

            if (!values.model && asset.model) {
                setFieldValue('model', asset.model)
            }
        }
    }, [asset, setFieldValue])

    useEffect(() => {
        const hasOneTouched = Object.keys(touched)?.length > 0
        if (hasOneTouched) {
            setError(null)
        }
    }, [touched])

    useEffect(() => {
        setError(null)
    }, [values])

    useEffect(() => {
        if (resetTouch) {
            setResetTouch(false)
            setTouched({})
        }
    }, [resetTouch])

    useEffect(() => {
        setIsValidForm(isValid)
    }, [isValid, setIsValidForm])

    // Update parent component when submission state changes
    useEffect(() => {
        if (trackSubmission) {
            trackSubmission(isSubmitting)
        }
    }, [isSubmitting, trackSubmission])

    const hasOneTouched = Object.keys(touched)?.length > 0

    const handleImageError = result => {
        const errorMessage =
            result.toString().indexOf('File is too large') > -1
                ? 'File is too large. 8MB Max'
                : result.toString().indexOf('422') > -1
                    ? result.toString().substring(10)
                    : 'Unable to upload file.'
        setError(errorMessage)
        changeLoading(false)
    }

    const onRemoveFile = index => {
        var imagesL = [...images]
        var photoIdsL = [...photosIds]

        imagesL.splice(index, 1)
        photoIdsL.splice(index, 1)

        setImages(imagesL)
        setPhotosIds(photoIdsL)
    }

    const onRemoveFileFunc = index => {
        var filesF = [...filesSuccess]
        filesF.splice(index, 1)
        setFileSuccess(filesF)
        onRemoveFile(index)
    }

    const handleImageSuccess = response => {
        const result = response?.data
        let imagesL = images
        let photoIdsL = photosIds

        imagesL.push({ path: result.filepath })
        photoIdsL.push(result.filename)

        setPhotosIds(photoIdsL)
        changeLoading(false)
    }

    const handleImageSuccessFunc = (resp, onAdded) => {
        onAdded()
        handleImageSuccess(resp)
    }

    const offlineImageUpload = (file, successAdded) => {
        setImages(prevImages => [...prevImages, file])
        successAdded?.push(file)
        changeLoading(false)
    }

    const imageUpload = (formData, handleImageError, successAdded, file) => {
        offlineImageUpload(file, successAdded)

        if (!isOffline) {
            handleUpdateFile(
                formData,
                handleImageSuccessFunc,
                handleImageError,
                () => {
                    successAdded?.push(file)
                },
            )
        }
    }

    const setFilesFunc = useCallback(
        async val => {
            if (
                filesSuccess?.length <= 1 &&
                val?.length <= 1 &&
                filesSuccess?.length + val?.length <= 1
            ) {
                let successAdded = []
                await Promise.all(
                    val?.map(file => {
                        const formData = new FormData()
                        formData.append('file', file)
                        changeLoading(true)
                        imageUpload(
                            formData,
                            handleImageError,
                            successAdded,
                            file,
                        )
                    }),
                )
                setFileSuccess([...filesSuccess, ...successAdded])
            } else {
                setError('You can not upload more than 1 photo')
                setTimeout(() => {
                    setError('') // Removing the error after 3 seconds
                }, 3000)
            }
        },
        [filesSuccess],
    )

    const formattedShiptos = selectOptions?.map(item => {
        return {
            ...item,
            locationToShow: (
                <CellLocation
                    isInCard
                    label={item?.label}
                    address={item?.address}
                />
            ),
        }
    })

    const renderImagesMemo = useMemo(() => {
        return filesSuccess?.map((item, index) => {
            const srcImage = URL.createObjectURL(item)
            return (
                <ImageItem
                    key={srcImage}
                    src={srcImage}
                    onRemove={() => onRemoveFileFunc(index)}
                />
            )
        })
    }, [filesSuccess])

    const renderPartsDetails = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start">
                <div className="flex lg:flex-row flex-col items-start justify-between gap-x-12 gap-y-1 w-full">
                    <InputText
                        {...getFieldProps('description')}
                        error={
                            (hasOneTouched &&
                                touched['description'] &&
                                errors['description']) ||
                            null
                        }
                        label="What do you need?"
                        isRequired
                        type="textarea"
                        placeholder="Please tell us the details of the part needed"
                        additionalClass="w-full flex-grow lg:rowSimilar"
                    />
                    <DropDown
                        customRender={({ label, address }) => (
                            <CellLocation
                                isInCard
                                label={label}
                                address={address}
                            />
                        )}
                        {...getFieldProps('shipToAccountId')}
                        data={formattedShiptos}
                        keyId="value"
                        keyToShow="locationToShow"
                        keyToFind="address"
                        setSelectedValue={selected => {
                            setFieldValue('shipToAccountId', selected?.value)
                        }}
                        selected={
                            selectOptions?.find(
                                s => s?.value === values?.shipToAccountId,
                            ) || null
                        }
                        name="selectLocation"
                        placeholder="Select Location"
                        label="Where do you need it?"
                        isSmall={!values?.shipToAccountId}
                        className="flex-grow w-full lg:rowSimilar"
                        required
                        error={
                            (hasOneTouched && errors['shipToAccountId']) || null
                        }
                    />
                </div>
                {!isMobile && <CustomDivider />}
                <div className="flex lg:flex-row flex-col items-start justify-between gap-x-12 w-full">
                    <InputText
                        {...getFieldProps('make')}
                        error={
                            (hasOneTouched &&
                                touched['make'] &&
                                errors['make']) ||
                            null
                        }
                        label="Make"
                        type="text"
                        placeholder="Add Make"
                        additionalClass="lg:rowSimilar"
                        isRequired
                    />
                    <InputText
                        {...getFieldProps('model')}
                        error={
                            (hasOneTouched &&
                                touched['model'] &&
                                errors['model']) ||
                            null
                        }
                        label="Model"
                        type="text"
                        placeholder="Add Model"
                        additionalClass="lg:rowSimilar"
                        isRequired
                    />
                </div>
                {!isMobile && <CustomDivider />}
                <div className="flex lg:flex-row flex-col items-start justify-between gap-x-12 gap-y-6 w-full">
                    <InputDate
                        {...getFieldProps('neededByDate')}
                        onChange={value => setFieldValue('neededByDate', value)}
                        label="When do you need it?"
                    />
                    <InputNumber
                        {...getFieldProps('quantity')}
                        onChange={value => setFieldValue('quantity', value)}
                        error={
                            (hasOneTouched &&
                                touched['quantity'] &&
                                errors['quantity']) ||
                            null
                        }
                        className="flex-grow lg:rowSimilar w-full"
                        label="How many?"
                    />
                </div>
            </div>
        )
    }

    const renderForm = () => {
        return (
            <div className="w-full h-auto flex flex-col items-center jusitfy-start lg:gap-y-6 gap-y-0">
                <RenderCustomCard
                    title="Parts Details"
                    content={renderPartsDetails()}
                />
                <div className="flex w-full flex-col lg:p-0 p-4 items-center justify-center transition-all">
                    <DragAndDrop setFiles={setFilesFunc} />
                    {filesSuccess?.length > 0 && (
                        <div className="w-full h-auto lg:flex grid grid-cols-3 items-center justify-start flex-wrap max-w-full mt-6 gap-x-4 gap-y-6">
                            {renderImagesMemo}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <form
            ref={formRef}
            onSubmit={handleFormSubmit}
            id="create-parts-request"
            className={`lg:w-240 w-auto h-auto m-auto ${isSubmitting ? 'opacity-70 pointer-events-none' : ''
                }`}
        >
            <CardInfo
                title="Add Requests Details"
                subTitle="Tell us what's the part you'd like to request"
                content={renderForm()}
                hasRounded={!isMobile && !isTablet}
                hasPaddingContent={!isMobile && !isTablet}
            />
        </form>
    )
}

CreatePartsQuoteForm.propTypes = {
    formRef: PropTypes.object,
    onSubmitForm: PropTypes.func,
    setError: PropTypes.func,
    setIsValidForm: PropTypes.func,
    asset: PropTypes.object,
    successfulSubmit: PropTypes.bool,
    setSuccessfulSubmit: PropTypes.func,
    resetTouch: PropTypes.bool,
    setResetTouch: PropTypes.func,
    images: PropTypes.array,
    photosIds: PropTypes.array,
    setImages: PropTypes.func,
    setPhotosIds: PropTypes.func,
    trackSubmission: PropTypes.func,
    error: PropTypes.string,
}

export default CreatePartsQuoteForm
